import Grid from '@mui/material/Grid';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ColorList from 'sharedComponents/commonHelper/ColorList';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { getToken } from 'store/CommonModuleState';
import { blackbookVehicleOptions, vehicleColorsLookup } from 'store/TradeInState';
import { RootState } from 'store/store';
import { colorStyles } from '../DigitalRetailingConstant';
import TradeInHeader from './header/TradeInHeader';
import { PROVINCE } from './province';
import { postLeadCreation } from 'store/LeadCreationState';
import { Lead } from 'pages/leadGen/LeadGenInterface';

const Step4: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);
  const hasRendered = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { token } = useSelector((state: RootState) => state.commonModuleStore);
  const { vehicleColorData, vehicleOptions } = useSelector((state: RootState) => state.tradeInStore);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const dealershipId = widgetData?.dealershipId ?? widgetData?.dealershipName;

  // React Hook Form
  const { watch, control, trigger, getValues } = useFormContext();

  const vehicleWatcher = watch('vehicleSearch');
  const provinceWatcher = watch('province');

  useEffect(() => {
    if (hasRendered.current) return;
    hasRendered.current = true;

    dispatch(
      vehicleColorsLookup({
        UVC: vehicleWatcher?.uvc,
      })
    );

    dispatch(
      blackbookVehicleOptions({
        UVC: vehicleWatcher?.uvc,
      })
    );

    if (!token && vehicleWatcher?.uvc) {
      dispatch(getToken(dealershipId));
    }
  }, [vehicleWatcher, dealershipId, dispatch, token]);

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { exteriorColor, interiorColor, mileage, province } = getValues();

    const vehicleData: Lead = {
      leadDetails: {
        'Interior Color': interiorColor,
        'Exterior Color': exteriorColor,
        Odometer: mileage,
        'Car Year': vehicleWatcher?.year,
        'Car Model': vehicleWatcher?.model,
        'Car Make': vehicleWatcher?.make,
        'Car Trim': vehicleWatcher?.trim,
        'Car UVC': vehicleWatcher?.uvc,
      },
      province: province,
    };

    console.log(vehicleData);

    dispatch(postLeadCreation({ data: vehicleData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (res?.payload?.success && (res?.payload?.data?.draftLeadId || res?.payload?.data?._id)) {
        onNext?.(vehicleOptions?.length > 0 ? 4 : 5);
      }
    });

    // onNext?.(vehicleOptions?.length > 0 ? 4 : 5);
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  const exteriorColors = vehicleColorData?.find((category) => category.name === 'Exterior Colours')?.color_list || [];
  // const leatherColors = vehicleColorData?.find((category) => category.name === 'Leather Colours')?.color_list || []; //  Temporarily removed
  const interiorColors = vehicleColorData?.find((category) => category.name === 'Interior Colours')?.color_list || [];

  return (
    <div className='dr-widget-steps d-flex flex-column h-100 step-2-1 position-relative'>
      <div className='dr-widget-steps__content'>
        <h2 className='dr-widget-steps__title'>{t('digitalRetailing.subHeading')}</h2>
        <TradeInHeader currentStep={0} />
        <div className='dr-widget-steps__form-wrapper'>
          <div className='tradein'>
            <h5 className='tradein__title'>{t('stepperFormTradeIn.vehicleInformation')} </h5>
            <div className='tradein__vehicle-info'>
              <label className='tradein__vehicle-info-lbl'>{t('stepperFormTradeIn.yourVehicle')}</label>
              <div className='tradein__vehicle-info-name'>{vehicleWatcher?.heading}</div>
            </div>

            {exteriorColors && exteriorColors.length > 0 ? (
              <div className='tradein__cp-box'>
                <label className='tradein__cp-box__lbl'>{t('stepperFormTradeIn.exteriorColour')}</label>
                <ColorList
                  colorList={exteriorColors}
                  control={control}
                  trigger={trigger}
                  name='exteriorColor'
                  t={t}
                  styles={colorStyles}
                />
              </div>
            ) : null}

            {interiorColors && interiorColors.length > 0 ? (
              <div className='tradein__cp-box'>
                <label className='tradein__cp-box__lbl'>{t('stepperFormTradeIn.exteriorColour')}</label>
                <ColorList
                  colorList={interiorColors}
                  control={control}
                  trigger={trigger}
                  name='interiorColor'
                  t={t}
                  styles={colorStyles}
                />
              </div>
            ) : null}

            <Grid container spacing={2}>
              <Grid item md={4} sm={7} xs={12}>
                <div className='form-group'>
                  <CommonInput
                    name='mileage'
                    control={control}
                    label={t('stepperFormTradeIn.vehicleMileage')}
                    placeholder={t('Enter')}
                    rules={formRequiredRules(errorMessage(t).vehicleMileageRequired)}
                    triggerValidationForField={trigger}
                    hint={t('stepperFormTradeIn.vehicleMileageNote')}
                    type='number'
                  />
                </div>
              </Grid>
              <Grid item md={4} sm={5} xs={12}>
                <SelectInput
                  name='province'
                  control={control}
                  label={t('stepperForm.province')}
                  rules={formRequiredRules(errorMessage(t).provinceRequired)}
                  options={PROVINCE}
                  triggerValidationForField={trigger}
                  defaultValue={provinceWatcher}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
      <div className='dealer-send-note text-secondary mt-auto'>{t('stepperFormTradeIn.note4')}</div>
    </div>
  );
};

const StepperForm4 = React.forwardRef(Step4);

export default StepperForm4;
